/* eslint-disable react/no-unused-prop-types */
import { ActionIcon, Box, Flex, Group, NumberInput, Stack, Text as MantineText } from '@mantine/core';
import { useCounter } from '@mantine/hooks';
import { IconMinus, IconPlus, IconTrash } from '@tabler/icons';
import { FC, ReactNode, useEffect } from 'react';

type Props = {
  initialCount?: number;
  min?: number;
  onChanged?: (count: number) => void;
  deleteDisabled?: boolean;
  onLastRemoved?: () => void;
  unit?: string;
  steps?: number[];
  formatValue?: (count: number) => ReactNode;
  accentColor?: string;
};

const IncDecCounter: FC<Props> = ({ initialCount = 0, min = 0, deleteDisabled, onChanged, onLastRemoved }) => {
  const [count, { increment, decrement, set }] = useCounter(initialCount ?? 0, { min });

  useEffect(() => {
    if (count !== initialCount) {
      onChanged?.(count);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  const showingTrashIcon = count <= min + 1;

  return (
    <Box
      sx={(theme) => ({
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[2],
        textAlign: 'center',
        width: 100,
        maxWidth: 150,
        padding: 0,
        borderRadius: theme.radius.md,
        cursor: 'pointer',
      })}
    >
      <Flex align="center" justify="center">
        <ActionIcon disabled={showingTrashIcon && deleteDisabled} variant="transparent" color="dark">
          {!showingTrashIcon && <IconMinus size={16} onClick={decrement} />}
          {showingTrashIcon && <IconTrash size={16} onClick={onLastRemoved} />}
        </ActionIcon>
        <NumberInput
          size="xs"
          hideControls
          value={count}
          onChange={set}
          sx={{ minWidth: 40, input: { textAlign: 'center', borderRadius: 0 }, justifyContent: 'center' }}
        />
        <ActionIcon
          variant="filled"
          color="blue"
          onClick={increment}
          sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
        >
          <IconPlus color="white" size={16} />
        </ActionIcon>
      </Flex>
    </Box>
  );
};

export const IncDecCounterv2: FC<Props> = ({
  unit,
  initialCount = 0,
  min = 0,
  onChanged,
  steps,
  formatValue,
  accentColor = '#1D5191',
}) => {
  const [count, { increment, decrement, set }] = useCounter(initialCount ?? 0, { min });
  const sortedSteps = steps ? [...steps].sort((a, b) => a - b) : undefined;
  const nextStep = sortedSteps?.find((step) => step > count);
  const previousStep = sortedSteps?.filter((step) => step < count).pop();
  const hasStepValues = !!sortedSteps;

  const handleIncrement = () => {
    if (nextStep !== undefined) {
      set(nextStep);
      return;
    }
    if (!hasStepValues) {
      increment();
    }
  };

  const handleDecrement = () => {
    if (previousStep !== undefined) {
      set(previousStep);
      return;
    }
    if (!hasStepValues) {
      decrement();
    }
  };

  useEffect(() => {
    if (count !== initialCount) {
      onChanged?.(count);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  return (
    <>
      <MantineText size="xs" weight={600} mb="md" sx={{ color: accentColor }}>{`Anzahl ${unit}`}</MantineText>

      <Group position="apart">
        <ActionIcon
          disabled={hasStepValues ? previousStep === undefined : count <= min}
          sx={(theme) => ({
            color: accentColor,
            backgroundColor: theme.fn.rgba(accentColor, theme.colorScheme === 'dark' ? 0.12 : 0.07),
            border: `1px solid ${theme.fn.rgba(accentColor, theme.colorScheme === 'dark' ? 0.32 : 0.24)}`,
            '&:disabled, &[data-disabled]': {
              color: theme.fn.rgba(accentColor, 0.42),
              backgroundColor: theme.fn.rgba(accentColor, theme.colorScheme === 'dark' ? 0.08 : 0.04),
              borderColor: theme.fn.rgba(accentColor, theme.colorScheme === 'dark' ? 0.18 : 0.14),
            },
          })}
          onClick={handleDecrement}
        >
          <IconMinus size={16} color="currentColor" />
        </ActionIcon>
        <Stack spacing={0} sx={{ alignItems: 'center' }}>
          <MantineText weight={600} size="lg" sx={{ color: accentColor }}>
            {formatValue ? formatValue(count) : count}
          </MantineText>
          <MantineText weight={500} size={10} sx={(theme) => ({ color: theme.fn.rgba(accentColor, 0.72) })}>
            {unit?.toUpperCase()}
          </MantineText>
        </Stack>

        <ActionIcon
          disabled={hasStepValues ? nextStep === undefined : false}
          sx={(theme) => ({
            color: accentColor,
            backgroundColor: theme.fn.rgba(accentColor, theme.colorScheme === 'dark' ? 0.12 : 0.07),
            border: `1px solid ${theme.fn.rgba(accentColor, theme.colorScheme === 'dark' ? 0.32 : 0.24)}`,
            '&:disabled, &[data-disabled]': {
              color: theme.fn.rgba(accentColor, 0.42),
              backgroundColor: theme.fn.rgba(accentColor, theme.colorScheme === 'dark' ? 0.08 : 0.04),
              borderColor: theme.fn.rgba(accentColor, theme.colorScheme === 'dark' ? 0.18 : 0.14),
            },
          })}
          onClick={handleIncrement}
        >
          <IconPlus size={16} color="currentColor" />
        </ActionIcon>
      </Group>
    </>
  );
};

export default IncDecCounter;
